import { Link } from "react-router-dom";
import { Lock, ScrollText, ShieldCheck, ArrowUpRight } from "lucide-react";
import { motion } from "framer-motion";

const ITEMS = [
  {
    icon: ShieldCheck,
    title: "HIPAA-conscious workflows",
    desc: "Built with the principles of HIPAA in mind, because dental records deserve that level of care.",
  },
  {
    icon: Lock,
    title: "Encryption everywhere",
    desc: "Your data is encrypted in transit and at rest.",
  },
  {
    icon: ScrollText,
    title: "Audit logs",
    desc: "Every action on a case is recorded, so you always know who did what and when.",
  },
];

export default function SecurityGrid({ showPageLink = true }) {
  return (
    <section data-testid="security-grid" className="relative py-16 sm:py-20">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14 items-start">
          <div className="lg:col-span-5">
            <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase font-semibold text-muted-foreground mb-4">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-brand-gradient" />
              Security
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-[-0.03em] leading-[1.05]">
              Security built into every case
            </h2>
            <p className="mt-4 text-sm sm:text-base text-muted-foreground max-w-md leading-relaxed">
              Dental data is sensitive, so Rxn3D is built with granular access, encryption everywhere, and a complete audit trail.
            </p>
            {showPageLink && (
              <div className="mt-8">
                <Link
                  to="/security"
                  data-testid="security-grid-see-page"
                  className="group inline-flex items-center gap-1.5 text-sm font-medium hairline rounded-full px-4 py-2 hover:bg-secondary transition-colors"
                >
                  See our full security page
                  <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
              </div>
            )}
          </div>

          <div className="lg:col-span-7 grid grid-cols-1 gap-3">
            {ITEMS.map((it, i) => {
              const Icon = it.icon;
              return (
                <motion.div
                  key={it.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: i * 0.05 }}
                  data-testid={`security-item-${i}`}
                  className="rounded-2xl hairline glass p-5"
                >
                  <div className="h-9 w-9 rounded-xl hairline inline-flex items-center justify-center mb-4">
                    <Icon className="h-4 w-4 text-brand-plum" />
                  </div>
                  <div className="font-heading font-semibold text-sm">{it.title}</div>
                  <div className="text-xs text-muted-foreground mt-1 leading-relaxed">{it.desc}</div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
